import React from "react";
import { Link } from "react-router-dom";
import {
  MoreHorizontal,
  ExternalLink,
  Trash2,
  BookOpen,
  MapPin,
} from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "components/ui/dropdown-menu";
import { numberRegex } from "Explore/Utils/numberRegex";
import { state_select } from "Explore/Config/FilterData";
import { ImageType, ImageCheckType } from "Explore/Config/ImageType";
import { ImageCheck } from "Explore/Components";

const getInitials = (name) => {
  if (!name) return "U";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
};

const formatNum = (val) => {
  if (val === undefined || val === null || val === "") return "-";
  return numberRegex(val);
};

const getStateLabel = (stateVal) => {
  if (!stateVal) return "";
  const found = state_select.find((s) => s.value === stateVal);
  return found?.label || stateVal;
};

const FavoritesListView = ({
  items,
  loading,
  entityType,
  onRemove,
}) => {
  if (loading) {
    if (entityType === "institutions") {
      return (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-xs">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/70 border-b border-slate-200">
                <TableHead className="w-[32%] font-semibold text-slate-700">Institution</TableHead>
                <TableHead className="w-[24%] font-semibold text-slate-700">Location</TableHead>
                <TableHead className="w-[34%] font-semibold text-slate-700">Top Academic Rankings</TableHead>
                <TableHead className="w-[10%] text-right font-semibold text-slate-700">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(6)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-lg" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Skeleton className="h-6 w-24 rounded-md" />
                      <Skeleton className="h-6 w-28 rounded-md" />
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="h-7 w-16 ml-auto rounded-md" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-xs">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50">
              <TableHead className="w-[30%]">Name</TableHead>
              <TableHead>Affiliation / Location</TableHead>
              <TableHead className="text-right">Publications</TableHead>
              <TableHead className="text-right">Citations</TableHead>
              <TableHead className="text-right">H-Index</TableHead>
              <TableHead className="text-right">Seniority / Authors</TableHead>
              <TableHead className="text-right w-[90px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...Array(6)].map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-9 w-9 rounded-full" />
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                </TableCell>
                <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-14 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto rounded-md" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  const getItemLink = (item) => {
    const slug = item.explore_slug || item.id;
    if (entityType === "researchers") return `/explore/faculty/${slug}`;
    if (entityType === "units") return `/explore/unit/${slug}`;
    if (entityType === "venues") return `/explore/venue/${item.id}`;
    return `/explore/institution/${slug}`;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-xs">
      <Table>
        <TableHeader>
          {entityType === "institutions" ? (
            <TableRow className="bg-gray-50/70 border-b border-gray-200">
              <TableHead className="w-[32%] font-semibold text-gray-700">Institution</TableHead>
              <TableHead className="w-[24%] font-semibold text-gray-700">Location</TableHead>
              <TableHead className="w-[34%] font-semibold text-gray-700">Top Academic Rankings</TableHead>
              <TableHead className="w-[10%] text-right font-semibold text-gray-700">Actions</TableHead>
            </TableRow>
          ) : (
            <TableRow className="bg-gray-50/70 border-b border-gray-200">
              <TableHead className="w-[30%] font-semibold text-gray-700">
                {entityType === "researchers"
                  ? "Faculty Member"
                  : entityType === "units"
                  ? "Unit Name"
                  : "Venue Name"}
              </TableHead>
              <TableHead className="font-semibold text-gray-700">
                {entityType === "researchers"
                  ? "Affiliation & Position"
                  : entityType === "units"
                  ? "Parent Institution"
                  : "Research Area"}
              </TableHead>
              <TableHead className="text-right font-semibold text-gray-700">
                Publications
              </TableHead>
              <TableHead className="text-right font-semibold text-gray-700">
                Citations
              </TableHead>
              <TableHead className="text-right font-semibold text-gray-700">
                {entityType === "venues" ? "Total Authors" : "H-Index"}
              </TableHead>
              <TableHead className="text-right font-semibold text-gray-700">
                {entityType === "researchers"
                  ? "Seniority"
                  : entityType === "venues"
                  ? "Total Institutions"
                  : "Total Faculties"}
              </TableHead>
              <TableHead className="text-right w-[90px] font-semibold text-gray-700">
                Actions
              </TableHead>
            </TableRow>
          )}
        </TableHeader>

        <TableBody>
          {items.map((item) => {
            const link = getItemLink(item);

            if (entityType === "institutions") {
              const rankings = Array.isArray(item.top_ranking_keywords)
                ? item.top_ranking_keywords
                : [];
              const locationStr =
                [item.city, getStateLabel(item.state)].filter(Boolean).join(", ") ||
                "United States";

              return (
                <TableRow
                  key={item.id}
                  className="hover:bg-gray-50/70 transition-colors"
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg border border-slate-200/90 flex-shrink-0 flex items-center justify-center p-1 bg-white shadow-2xs">
                        <ImageCheck
                          url={`https://media.academic.online/institutions_images/${item.id}/${ImageType.SEAL}.png`}
                          type={ImageCheckType.INSTITUTION_LOGO}
                        />
                      </div>
                      <div className="min-w-0">
                        <Link
                          to={link}
                          className="font-bold text-explore hover:text-explore/80 hover:underline block text-sm leading-snug break-words"
                          title={item.name}
                        >
                          {item.name}
                        </Link>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-1.5 text-xs text-slate-600 font-medium">
                      <MapPin size={13} className="text-explore flex-shrink-0" />
                      <span className="truncate" title={locationStr}>
                        {locationStr}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5 py-0.5">
                      {rankings.length > 0 ? (
                        <>
                          {rankings.slice(0, 3).map((kw, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-50 border border-slate-200/80 text-xs hover:bg-slate-100 transition-colors"
                            >
                              <span className="font-bold text-explore text-[11px] tabular-nums">
                                #{kw.ranking}
                              </span>
                              <span
                                className="text-slate-700 font-medium capitalize truncate max-w-[150px]"
                                title={kw.keyword}
                              >
                                {kw.keyword}
                              </span>
                            </span>
                          ))}
                          {rankings.length > 3 && (
                            <span className="text-[11px] font-semibold text-slate-400 px-1.5 py-0.5">
                              +{rankings.length - 3} more
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-slate-400 italic">
                          Leading research institution
                        </span>
                      )}
                    </div>
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Link
                        to={link}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-explore hover:text-white hover:bg-explore bg-explore/5 border border-explore/20 rounded-md transition-colors"
                        title="View profile"
                      >
                        <span>View</span>
                        <ExternalLink size={12} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => onRemove(item.id)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                        title="Remove from favorites"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            }

            const avatarUrl = item.photo_url || item.logo_url || null;
            const publications = item.publication_count ?? item.total_publication ?? item.publications;
            const citations = item.citation_count ?? item.total_citation ?? item.citations;
            const hIndex = item.h_index ?? item.total_h_index ?? item.hIndex;
            const seniority = item.seniority;
            const faculties = item.total_faculties ?? item.faculties;
            const authors = item.total_authors;
            const venueInstitutions = item.total_institutions;

            return (
              <TableRow key={item.id} className="hover:bg-gray-50/70 transition-colors">
                <TableCell>
                  <div className="flex items-center gap-3">
                    {entityType === "units" ? (
                      <div className="h-9 w-9 rounded-md border border-gray-200 flex-shrink-0 flex items-center justify-center p-0.5 bg-white">
                        <ImageCheck
                          url={`https://media.academic.online/institutions_images/${item.institution_id}/${ImageType.LANDSCAPE}.png`}
                          type={ImageCheckType.INSTITUTION_LOGO}
                        />
                      </div>
                    ) : (
                      <Avatar className="h-9 w-9 rounded-md border border-gray-200 flex-shrink-0">
                        {avatarUrl && <AvatarImage src={avatarUrl} alt={item.name} />}
                        <AvatarFallback className="bg-explore/10 text-explore text-xs font-semibold">
                          {entityType === "venues" ? (
                            <BookOpen size={16} />
                          ) : (
                            getInitials(item.name)
                          )}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className="min-w-0">
                      <Link
                        to={link}
                        className="font-bold text-explore hover:text-explore/80 hover:underline block text-sm"
                        title={item.name}
                      >
                        {item.name}
                      </Link>
                      {item.position && entityType === "researchers" && (
                        <p
                          className="text-xs text-explore font-medium mt-0.5 leading-snug break-words"
                          title={item.position}
                        >
                          {item.position}
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>

                <TableCell className="text-gray-600 text-xs">
                  {entityType === "researchers" ? (
                    <span className="truncate block max-w-xs font-medium text-slate-600">
                      {item.institution_name || "-"}
                    </span>
                  ) : entityType === "units" ? (
                    <span className="truncate block max-w-xs font-medium text-explore">
                      {item.institution_name || "-"}
                    </span>
                  ) : (
                    <span className="truncate block max-w-xs text-gray-500">
                      {Array.isArray(item.keywords) ? item.keywords.slice(0, 3).join(", ") : "-"}
                    </span>
                  )}
                </TableCell>

                <TableCell className="text-right text-xs font-bold text-gray-800">
                  {formatNum(publications)}
                </TableCell>

                <TableCell className="text-right text-xs font-bold text-gray-800">
                  {formatNum(citations)}
                </TableCell>

                <TableCell className="text-right text-xs font-bold text-gray-800">
                  {entityType === "venues" ? formatNum(authors) : formatNum(hIndex)}
                </TableCell>

                <TableCell className="text-right text-xs font-bold text-gray-800">
                  {entityType === "researchers"
                    ? (seniority ? `${seniority} yrs` : "-")
                    : entityType === "venues"
                    ? formatNum(venueInstitutions)
                    : formatNum(faculties)}
                </TableCell>

                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => onRemove(item.id)}
                      className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                      title="Remove from favorites"
                    >
                      <Trash2 size={15} />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-gray-900 rounded-md"
                        >
                          <MoreHorizontal size={15} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40 bg-white shadow-lg border-gray-200">
                        <DropdownMenuItem asChild className="text-xs cursor-pointer py-2">
                          <Link to={link} className="flex items-center gap-2">
                            <ExternalLink size={14} className="text-gray-500" />
                            View Profile
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onRemove(item.id)}
                          className="text-xs cursor-pointer py-2 text-rose-600 focus:text-rose-700 focus:bg-rose-50 flex items-center gap-2"
                        >
                          <Trash2 size={14} />
                          Remove Favorite
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default FavoritesListView;
